import Reveal from '@/components/base/Reveal';

interface EngineStage {
  index: string;
  tag: string;
  title: string;
  icon: string;
  tone: 'secondary' | 'primary' | 'accent';
  description: string;
  points: string[];
  status: string;
}

const stages: EngineStage[] = [
  {
    index: '01',
    tag: 'INPUT LAYER',
    title: 'Data Ingestion',
    icon: 'ri-radar-line',
    tone: 'secondary',
    description:
      'The EA reads the XAUUSD feed directly from the MT5 terminal and keeps a live picture of the market — price, spread, session and volatility — instead of reacting to a single candle.',
    points: [
      'Live XAUUSD bid / ask price ticks',
      'Spread and liquidity conditions',
      'Trading session and timing context',
      'Volatility and momentum state',
    ],
    status: 'FEED · XAUUSD',
  },
  {
    index: '02',
    tag: 'CORE LOGIC',
    title: 'The Filtering Engine',
    icon: 'ri-filter-3-line',
    tone: 'primary',
    description:
      'Every reading is tested against a predefined rule set. The engine does not interpret, guess or override — it returns a pass or a reject, and a rejected setup is discarded without a trade.',
    points: [
      'Fixed, predefined condition set',
      'Trend and structure alignment',
      'Volatility and spread thresholds',
      'Risk gate: size, exposure, stop distance',
    ],
    status: 'RULE SET · 4 CHECKS',
  },
  {
    index: '03',
    tag: 'OUTPUT LAYER',
    title: 'Precision Execution',
    icon: 'ri-crosshair-2-line',
    tone: 'accent',
    description:
      'Only when every check passes does the EA act. The order is sent through MT5 with its entry, stop loss and take profit defined at placement, then handed straight to position management.',
    points: [
      'Order routed through the MT5 terminal',
      'Entry, stop loss, take profit set at placement',
      'Position size derived from risk rules',
      'Position handed to management logic',
    ],
    status: 'MT5 · MARKET ORDER',
  },
];

const toneStyles = {
  secondary: {
    bar: 'bg-secondary-500',
    tag: 'bg-secondary-100 text-secondary-900',
    icon: 'bg-secondary-100 text-secondary-700',
    check: 'text-secondary-600',
    value: 'text-secondary-700',
  },
  primary: {
    bar: 'bg-primary-500',
    tag: 'bg-primary-100 text-primary-800',
    icon: 'bg-primary-100 text-primary-700',
    check: 'text-primary-600',
    value: 'text-primary-700',
  },
  accent: {
    bar: 'bg-accent-500',
    tag: 'bg-accent-100 text-accent-900',
    icon: 'bg-accent-100 text-accent-700',
    check: 'text-accent-700',
    value: 'text-accent-800',
  },
};

function StageCard({ stage }: { stage: EngineStage }) {
  const t = toneStyles[stage.tone];
  const isCore = stage.tone === 'primary';

  return (
    <article
      className={`relative h-full flex flex-col overflow-hidden rounded-lg border transition-all duration-300 hover:-translate-y-1 ${
        isCore ? 'border-primary-300 bg-primary-50/40' : 'border-background-300 bg-background-50'
      }`}
    >
      <span className={`h-1 w-full ${t.bar}`}></span>

      <div className="flex flex-1 flex-col p-6 md:p-7">
        <div className="flex items-center justify-between">
          <span className="font-data text-[26px] font-semibold leading-none text-foreground-950">
            {stage.index}
          </span>
          <span
            className={`inline-flex h-7 items-center rounded-full px-3 text-[10px] font-data tracking-[0.18em] ${t.tag}`}
          >
            {stage.tag}
          </span>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <span className={`w-11 h-11 rounded-md flex items-center justify-center ${t.icon}`}>
            <i className={`${stage.icon} text-[22px]`}></i>
          </span>
          <h3 className="font-heading font-bold text-foreground-950 text-[19px] leading-tight">
            {stage.title}
          </h3>
        </div>

        <p className="mt-4 text-[14px] leading-relaxed text-foreground-600">
          {stage.description}
        </p>

        <ul className="mt-5 flex-1 space-y-2.5 border-t border-background-200 pt-5">
          {stage.points.map((point) => (
            <li key={point} className="flex items-start gap-2.5 text-[13px] text-foreground-700">
              <span className="w-4 h-4 flex items-center justify-center shrink-0 mt-[1px]">
                <i className={`ri-check-line ${t.check} text-[14px]`}></i>
              </span>
              <span>{point}</span>
            </li>
          ))}
        </ul>

        <div className="mt-6 flex items-center justify-between rounded-md border border-background-200 bg-background-100 px-3.5 py-2.5">
          <span className="text-[10px] font-data tracking-[0.18em] text-foreground-400">STATUS</span>
          <span className={`text-[11px] font-data tracking-wider ${t.value}`}>{stage.status}</span>
        </div>
      </div>
    </article>
  );
}

function EngineAnatomy() {
  return (
    <section
      id="engine"
      className="w-full bg-background-100 border-y border-background-200 px-4 md:px-8 lg:px-12 py-16 md:py-24"
    >
      <div className="max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 items-end">
          <Reveal>
            <div className="flex items-center gap-3 mb-5">
              <span className="text-[11px] font-data tracking-[0.24em] text-primary-600">
                INSIDE THE ENGINE
              </span>
              <span className="h-px w-10 bg-primary-400"></span>
              <span className="text-[11px] font-data tracking-[0.2em] text-foreground-400">
                DATA → FILTER → EXECUTION
              </span>
            </div>
            <h2 className="font-heading font-extrabold tracking-tight text-foreground-950 text-[32px] leading-[1.08] sm:text-[42px] lg:text-[46px]">
              What The EA Reads, Filters And Executes.
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-[15px] md:text-base leading-relaxed text-foreground-600 lg:pb-2 max-w-[560px]">
              Before a position exists, the EA moves through three technical stages. It ingests live
              market data, filters that data against a fixed rule set, and only then executes. No
              stage can be skipped, and nothing is decided on a feeling.
            </p>
          </Reveal>
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-6">
          {stages.map((stage, idx) => (
            <div key={stage.title} className="relative">
              <Reveal delay={idx * 120}>
                <StageCard stage={stage} />
              </Reveal>
              {idx < stages.length - 1 && (
                <span className="hidden lg:flex absolute top-1/2 -right-3 -translate-y-1/2 z-10 w-6 h-6 items-center justify-center rounded-full border border-background-300 bg-background-100 text-foreground-400">
                  <i className="ri-arrow-right-s-line text-[16px]"></i>
                </span>
              )}
            </div>
          ))}
        </div>

        <Reveal delay={220}>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-lg border border-background-200 bg-background-50 px-5 md:px-6 py-4">
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
              <span className="flex items-center gap-2 font-data text-[11px] tracking-wider text-profit-600">
                <span className="w-1.5 h-1.5 rounded-full bg-profit-500"></span>
                PASS → EXECUTION
              </span>
              <span className="flex items-center gap-2 font-data text-[11px] tracking-wider text-foreground-500">
                <span className="w-1.5 h-1.5 rounded-full bg-foreground-400"></span>
                REJECT → BACK TO INGESTION
              </span>
            </div>
            <span className="text-center sm:text-right text-[11px] font-data tracking-[0.14em] text-foreground-400">
              NO DATA WITHOUT A FILTER · NO FILTER WITHOUT A RULE · NO TRADE WITHOUT A PASS
            </span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default EngineAnatomy;